import React from 'react';
import {Field} from 'react-final-form';
import {compose} from 'recompose';
import {
    FormFieldCheckbox,
    FormFieldRadioButton,
    FormFieldSelect,
} from '@yandex-data-ui/cloud-components';
import {ElasticsearchEdition} from '@yandex-data-ui/cloud-schemas/build/models/elasticsearch';
import {MDBDiskType} from '@yandex-data-ui/cloud-schemas/build/models/mdb';
import withTranslation, {WithTranslationProps} from 'hoc/withTranslation';

import {findClosest, getValueInRange} from 'utils';
import CalculatorFormLayout from 'components/calculator/CalculatorFormLayout/CalculatorFormLayout';
import HostsInput from 'components/calculator/MDBForm/MDBFormCommon/HostsInput';
import SizeRangeInput from 'components/calculator/MDBForm/MDBFormCommon/SizeRangeInput';
import SizeRangeStepInput from 'components/calculator/MDBForm/MDBFormCommon/SizeRangeStepInput';
import {getMemSizeLabel, checkServiceType} from 'components/calculator/MDBForm/utils';
import {MDBFormValues} from 'components/calculator/MDBForm/MDBForm';
import {
    MDBPreparedConfig,
    MDBPreparedPreset,
    ZookeeperHost,
} from 'components/calculator/MDBForm/types';
import withFinalForm, {WithFinalForm} from 'hoc/withFinalForm';
import {
    ClusterType,
    getPlatformOptions,
    getHostTypeOptions,
} from 'components/calculator/MDBForm/constants';
import TrafficField from 'components/calculator/Traffic/TrafficField/TrafficField';
import {toSelectItems} from 'components/formFields/utils';

export interface MDBFormItemValues {
    edition?: string;
    platform: string;
    disk: string;
    size: number;
    presetId: string;
    hosts: number;
    ip: boolean;
    traffic: number;
    type: string;
    zookeeper?: ZookeeperHost[];
}

interface MDBFormItemOwnProps {
    index: number;
    data: MDBPreparedConfig;
    name: string;
    values: MDBFormItemValues;
    service: string;
}

interface OptionsProps {
    content: string;
    value: string;
}

interface MDBFormItemState {
    editions?: OptionsProps[];
    platforms: OptionsProps[];
    presets: {
        presetId: string;
        diskTypes: string[];
        content: string;
        value: string;
    }[];
    hostTypes: OptionsProps[];
    hostsRange: Pick<MDBDiskType, 'minHosts' | 'maxHosts'>;
    diskSizeRange: MDBDiskType['diskSizeRange'];
    diskTypes: string[];
    diskSizes: MDBDiskType['diskSizes'];
}

type MDBFormItemProps = MDBFormItemOwnProps & WithTranslationProps & WithFinalForm<MDBFormValues>;

class MDBFormItem extends React.Component<MDBFormItemProps, MDBFormItemState> {
    constructor(props: MDBFormItemProps) {
        super(props);

        const {
            values: {platform, disk, presetId, type},
            data: {availableEditions, availableGenerations},
        } = props;

        const editions = this.getEditions(availableEditions);
        const platforms = this.getPlatforms(availableGenerations);
        const hostTypes = this.getHostTypes(platform);
        const presets = this.getPresets(platform, type);
        const preset = this.getPreset(presets, presetId);
        const diskTypes = this.getDiskTypes(preset as MDBPreparedPreset);
        const {hostsRange, diskSizeRange, diskSizes} = this.getDiskInfo(presetId, disk);

        this.state = {
            editions,
            platforms,
            presets,
            hostTypes,
            hostsRange,
            diskSizeRange,
            diskTypes,
            diskSizes,
        };
    }

    render() {
        if (!this.props.values) {
            return null;
        }

        const {
            name,
            service,
            values: {hosts},
            i18nK,
        } = this.props;

        const {presets, diskTypes, diskSizes, diskSizeRange, hostsRange, hostTypes, platforms} =
            this.state;
        const hostsLabels = [i18nK('field_mdb-hosts-label', {count: hostsRange.maxHosts})];

        if (checkServiceType(service, ClusterType.CLICKHOUSE) && hosts > 1) {
            hostsLabels.push(i18nK('field_mdb-hosts-zookeeper-label'));
        }

        return (
            <React.Fragment>
                {this.renderEditions()}
                <CalculatorFormLayout.Row
                    title={i18nK('field_mdb-platform')}
                    note={i18nK('context_mdb-platform')}
                >
                    <Field name={`${name}.platform`}>
                        {(fieldProps) => (
                            <FormFieldSelect<'single'>
                                {...fieldProps}
                                size="n"
                                items={toSelectItems(platforms)}
                                onUpdate={this.handlePlatformChange}
                            />
                        )}
                    </Field>
                </CalculatorFormLayout.Row>
                <CalculatorFormLayout.Row
                    title={i18nK('field_mdb-type')}
                    note={i18nK('context_mdb-type')}
                >
                    <Field name={`${name}.type`}>
                        {(fieldProps) => (
                            <FormFieldRadioButton
                                {...fieldProps}
                                size="l"
                                options={hostTypes}
                                onUpdate={this.handleTypeChange}
                                width="max"
                            />
                        )}
                    </Field>
                </CalculatorFormLayout.Row>
                <CalculatorFormLayout.Row
                    title={i18nK('field_mdb-preset')}
                    note={i18nK('context_mdb-preset')}
                >
                    <Field name={`${name}.presetId`}>
                        {(fieldProps) => (
                            <FormFieldSelect<'single'>
                                {...fieldProps}
                                size="n"
                                items={toSelectItems(presets)}
                                onUpdate={this.handlePresetChange}
                            />
                        )}
                    </Field>
                </CalculatorFormLayout.Row>
                <CalculatorFormLayout.Row
                    title={i18nK('field_mdb-disk')}
                    note={i18nK('context_mdb-disk')}
                >
                    <Field name={`${name}.disk`}>
                        {(fieldProps) => (
                            <FormFieldSelect<'single'>
                                {...fieldProps}
                                size="n"
                                items={diskTypes.map((type) => ({title: type, value: type}))}
                                onUpdate={this.handleDiskChange}
                            />
                        )}
                    </Field>
                </CalculatorFormLayout.Row>
                <CalculatorFormLayout.Row
                    title={i18nK('field_mdb-size')}
                    note={i18nK('context_mdb-size')}
                >
                    {diskSizes ? (
                        <SizeRangeStepInput
                            steps={diskSizes.sizes.map(Number)}
                            name={`${name}.size`}
                            infoPointsCount={2}
                            size="l"
                        />
                    ) : (
                        <SizeRangeInput
                            min={Number(diskSizeRange?.min)}
                            max={Number(diskSizeRange?.max)}
                            name={`${name}.size`}
                            size="l"
                        />
                    )}
                </CalculatorFormLayout.Row>
                <HostsInput
                    name={name}
                    title={i18nK('field_mdb-hosts')}
                    note={this.getHostsNote()}
                    hostsRange={hostsRange}
                    handleChange={this.handleHostsChange}
                    labels={hostsLabels}
                />
                <CalculatorFormLayout.Row
                    title={i18nK('field_mdb-ip')}
                    note={i18nK('context_mdb-ip')}
                >
                    <Field name={`${name}.ip`}>
                        {(fieldProps) => <FormFieldCheckbox {...fieldProps} size="l" />}
                    </Field>
                </CalculatorFormLayout.Row>
                <TrafficField name={`${name}.traffic`} />
            </React.Fragment>
        );
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    private changeFormValues = (values: Array<{name: string; value: any}>) => {
        const {
            index,
            formApi: {change},
        } = this.props;

        values.forEach(({name, value}) => {
            change(`items[${index}].${name}` as keyof MDBFormValues, value);
        });
    };

    private handlePlatformChange = (newPlatform?: string) => {
        const {type, presetId, disk} = this.props.values;

        if (!newPlatform) {
            return;
        }

        const {newState, newValues} = this.getTypeChangeData(newPlatform, type, presetId, disk);

        this.changeFormValues(newValues);
        this.setState(newState as MDBFormItemState);
    };

    private handleTypeChange = (newType: string) => {
        const {platform, presetId, disk} = this.props.values;

        const {newState, newValues} = this.getTypeChangeData(platform, newType, presetId, disk);

        this.changeFormValues(newValues);
        this.setState(newState as MDBFormItemState);
    };

    private handlePresetChange = (newPreset?: string) => {
        const {disk} = this.props.values;

        if (!newPreset) {
            return;
        }

        const {newState, newValues} = this.getPresetChangeData(newPreset, disk);

        this.changeFormValues(newValues);
        this.setState(newState as MDBFormItemState);
    };

    private handleDiskChange = (newDisk?: string) => {
        const {platform, type, presetId} = this.props.values;

        if (!newDisk) {
            return;
        }

        const {newState, newValues} = this.getPlatformChangeData(platform, type, presetId, newDisk);

        this.changeFormValues(newValues);
        this.setState(newState as MDBFormItemState);
    };

    private handleHostsChange = (newHosts: number) => {
        const {service} = this.props;

        if (checkServiceType(service, ClusterType.CLICKHOUSE)) {
            const value = newHosts > 1 ? this.props.data.zookeeper : [];
            this.changeFormValues([{name: 'zookeeper', value}]);
        }
    };

    private getEditions(availableEditions?: ElasticsearchEdition[]) {
        return (
            availableEditions && availableEditions.map(({id, name}) => ({content: name, value: id}))
        );
    }

    private getPlatforms(availableGenerations: Set<string>) {
        const {i18n} = this.props;
        return getPlatformOptions(i18n).filter(({value}) => availableGenerations.has(value));
    }

    private getHostTypes(platform: string) {
        const {
            data: {availableHostTypes},
            i18n,
        } = this.props;

        return getHostTypeOptions(i18n).filter(({value: type}) => {
            const presets = this.getPresets(platform, type);
            return presets.length && availableHostTypes.has(type);
        });
    }

    private getTypeChangeData(platform: string, type: string, presetId: string, disk: string) {
        const hostTypes = this.getHostTypes(platform);
        const hostTypesArray = hostTypes.map(({value}) => value);
        const newType = hostTypesArray.includes(type) ? type : hostTypesArray[0];
        const presets = this.getPresets(platform, newType);
        const newPreset = this.getPreset(presets, presetId);
        const diskTypes = this.getDiskTypes(newPreset as MDBPreparedPreset);
        const newDiskType = this.getDiskType(diskTypes, disk);

        const {newState, newValues} = this.getPlatformChangeData(
            platform,
            newType,
            presetId,
            newDiskType,
        );
        newState.diskTypes = diskTypes;
        newState.hostTypes = hostTypes;

        return {newState, newValues};
    }

    private getPlatformChangeData(platform: string, type: string, presetId: string, disk: string) {
        const presets = this.getPresets(platform, type);
        const newPreset = this.getPreset(presets, presetId);
        const {newState, newValues} = this.getPresetChangeData(newPreset.presetId, disk);

        newState.presets = presets;
        newValues.push({name: 'presetId', value: newPreset.presetId});
        if (disk) {
            newValues.push({name: 'disk', value: disk});
        }

        return {newState, newValues};
    }

    private getPresetChangeData(
        presetId: string,
        diskType: string,
    ): {
        newState: Partial<MDBFormItemState>;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        newValues: {name: string; value: any}[];
    } {
        const {size, hosts} = this.props.values;
        const {presets} = this.state;

        const preset = this.getPreset(presets, presetId);
        const diskTypes = this.getDiskTypes(preset as MDBPreparedPreset);
        const newDiskType = this.getDiskType(diskTypes, diskType);
        const {diskSizeRange, diskSizes, hostsRange} = this.getDiskInfo(presetId, newDiskType);
        const newSize = diskSizeRange
            ? getValueInRange(diskSizeRange, size)
            : findClosest(diskSizes?.sizes || [], size);
        const newHosts = getValueInRange(
            {min: hostsRange.minHosts, max: hostsRange.maxHosts},
            Number(hosts),
        );

        return {
            newState: {
                diskTypes,
                diskSizeRange,
                diskSizes,
                hostsRange,
            },
            newValues: [
                {name: 'disk', value: newDiskType},
                {name: 'size', value: newSize},
                {name: 'hosts', value: newHosts},
            ],
        };
    }

    private getPresets(platform: string, type: string) {
        const {presets, resourcePresets} = this.props.data;
        return resourcePresets
            .filter((presetKey) => {
                const [presetHostType, presetName] = presetKey.split('==');
                return presetHostType === type && presetName.includes(`${platform}.`);
            })
            .map((presetKey) => {
                const {presetId, memoryLimit, cpuLimit, diskTypes, cpuFraction} =
                    presets[presetKey];

                return {
                    presetId,
                    diskTypes,
                    content: this.getPresetName(presetId, cpuLimit, memoryLimit, cpuFraction),
                    value: presetId,
                };
            });
    }

    private getPreset<T extends Array<{presetId: string}>>(presets: T, currentPresetId: string) {
        return presets.find(({presetId}) => presetId === currentPresetId) || presets[0];
    }

    private getPresetName(id: string, cpu: number, memory: number, cpuFraction: number) {
        const {i18n, i18nK} = this.props;

        const memoryFromBytes = getMemSizeLabel(memory, i18n);
        const memLabel = i18nK('field_memory_limit_value', {memory: memoryFromBytes});
        const cpuLabel = i18nK('field_cpu_limit_value', {count: cpu});
        const units = i18nK('field_cpu_units', {count: cpu});
        const fractionLabel = id.startsWith('b') ? `, ${cpuFraction}%` : '';

        return `${id} (${cpuLabel} ${units} ${fractionLabel} / ${memLabel})`;
    }

    private getDiskTypes(preset: {diskTypes: string[]}) {
        return preset.diskTypes.map((diskType) => diskType.split('==')[1]);
    }

    private getDiskType(diskTypes: string[], currentDiskType: string) {
        return diskTypes.find((type) => type === currentDiskType) || diskTypes[0];
    }

    private getDiskInfo(presetId: string, diskTypeId: string) {
        const {diskTypes} = this.props.data;
        const {minHosts, maxHosts, diskSizeRange, diskSizes} =
            diskTypes[`${presetId}==${diskTypeId}`];
        return {
            hostsRange: {
                minHosts,
                maxHosts,
            },
            diskSizeRange,
            diskSizes,
        };
    }

    private getHostsNote = () => {
        const {service, i18nK} = this.props;
        const {MONGODB, CLICKHOUSE} = ClusterType;

        return checkServiceType(service, MONGODB) || checkServiceType(service, CLICKHOUSE)
            ? i18nK('context_mdb-hosts-shard')
            : i18nK('context_mdb-hosts');
    };

    private renderEditions() {
        const {name, i18nK} = this.props;
        const {editions} = this.state;

        if (!Array.isArray(editions) || editions.length <= 0) {
            return null;
        }

        return (
            <CalculatorFormLayout.Row
                title={i18nK('field_mdb-edition')}
                note={i18nK('context_mdb-edition')}
            >
                <Field name={`${name}.edition`}>
                    {(fieldProps) => (
                        <FormFieldRadioButton
                            {...fieldProps}
                            size="l"
                            options={editions}
                            width="max"
                        />
                    )}
                </Field>
            </CalculatorFormLayout.Row>
        );
    }
}

export default compose<MDBFormItemProps, MDBFormItemOwnProps>(
    withFinalForm<MDBFormValues>(),
    withTranslation('calculator'),
)(MDBFormItem);
